import React, { useEffect, useState, useCallback } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import { Button, Card, Form, Nav } from 'react-bootstrap';
import Table from '../../../components/Table';
import ConfirmDialog from './confirmDialog';
import MediciensModel from './mediciensModel';
import { resetMedicens } from '../../../redux/mediciens/slice';
import { getMedicienDetails, getMediciens } from '../../../redux/mediciens';
import useDebounce from '../../../utils/useDebounceHook';
import { useMedicineColumns } from './hooks';

const Mediciens = () => {
	const [key, setKey] = useState('approved');
	const [perPage, setPerPage] = useState(10);
	const [show, setShow] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchText, setSearchText] = useState('');
	const [columnSort, setColumnSort] = useState({});
	const [confirmData, setConfirmData] = useState(null);
	const search = useDebounce(searchText, 500);
	const { loading, mediciens, totalMediciens } = useSelector(state => state.mediciens);
	const dispatch = useDispatch();

	useEffect(() => {
		fetchMedicines();
		return () => {
			dispatch(resetMedicens());
		};
	}, [currentPage, perPage, search, columnSort, key, dispatch]);

	const changeKey = useCallback((key) => {
		setSearchText('');
		setCurrentPage(1);
		setPerPage(10);
		setColumnSort({});
		setKey(key);
	}, []);

	const fetchMedicines = useCallback(() => {
		dispatch(getMediciens({ key, page: currentPage, per_page: perPage, search, ...columnSort }));
	}, [currentPage, perPage, search, key, columnSort, dispatch]);

	const handlePageChange = useCallback(page => {
		setCurrentPage(page);
	}, []);

	const handlePerRowsChange = useCallback(async(newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1);
	}, []);

	const handleSort = useCallback(
		(column, sortDirection) => {
			setColumnSort({ column_name: column.sortField, order: sortDirection });
		},
		[setColumnSort]
	);

	const fetchMedicinesDetails = useCallback(
		(id) => {
			dispatch(getMedicienDetails({ id }))
				.unwrap()
				.then(() => setShow(true));
		},
		[dispatch]
	);

	const columns = useMedicineColumns({ key, currentPage, perPage, approval: setConfirmData, t, handleEdit: fetchMedicinesDetails });


	return (
		<Card>
			<Card.Header className='bg-white fw-bold d-flex justify-content-between align-items-center'>
				<span>{t('MEDICIENS.TITLE')}</span>
				<div className='d-flex align-items-center'>
					<Form.Control
						type="search"
						placeholder={t('GENERAL.SEARCHTEXT')}
						size="sm"
						className='form-control d-inline-block w-auto me-2'
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
					/>
					{key === 'approved' && (
						<Button onClick={() => setShow(true)} variant="success" size="sm" className='d-inline-flex align-items-center'>
							<FaPlus className='me-1' /> {t('MEDICIENS.ADD_MEDICINE')}
						</Button>
					)}
				</div>
			</Card.Header>
			<Card.Body>
				<Nav variant="tabs" activeKey={key} onSelect={(k) => changeKey(k)} className="mb-3">
					<Nav.Item>
						<Nav.Link eventKey="approved">{t('MEDICIENS.NAV.MEDICINE')}</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link eventKey="pending">{t('MEDICIENS.NAV.PENDING_MEDICINE')}</Nav.Link>
					</Nav.Item>
				</Nav>
				<Table
					columns={columns}
					data={mediciens}
					loading={loading}
					handlePageChange={handlePageChange}
					perPage={perPage}
					handlePerRowsChange={handlePerRowsChange}
					total={totalMediciens}
					handleSort={handleSort}
				/>
			</Card.Body>
			<MediciensModel
				show={show}
				close={() => setShow(false)}
				fetchMedicines={fetchMedicines}
			/>
			<ConfirmDialog
				payload={confirmData}
				handleClose={() => setConfirmData(null)}
				fetchMedicines={fetchMedicines}
			/>
		</Card>
	);

};

export default Mediciens;

